import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import api from '../lib/api';
import {
  Learner,
  LearnerGroup,
  GroupDetail,
  LearnerRole,
  LearnerLocation,
  LearnerDepartment,
  LearnersListResponse,
  GroupsListResponse,
  GroupDetailResponse,
  BulkImportResponse,
  LearnersQueryParams,
  GroupsQueryParams,
  LearnerCreateData,
  LearnerUpdateData,
  LearnerActionData,
  BulkImportLearnerData,
  GroupCreateData,
  GroupUpdateData,
  AddGroupMemberData,
} from '../types/learner';
import { ApiResponse, SuccessResponse, Pagination } from '../types/api';

// ─── Learners ───────────────────────────────────────────────────────────

/**
 * Fetch paginated list of learners
 * GET /api/v1/learners
 */
export const fetchLearners = async (
  params: LearnersQueryParams = {}
): Promise<{ learners: Learner[]; pagination: Pagination }> => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.group_id) queryParams.append('group_id', params.group_id);
  if (params.department_id) queryParams.append('department_id', params.department_id);
  if (params.role_id) queryParams.append('role_id', params.role_id);
  if (params.location_id) queryParams.append('location_id', params.location_id);
  if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);
  if (params.sort_order) queryParams.append('sort_order', params.sort_order);

  const response = await api.get<LearnersListResponse>(`/learners?${queryParams.toString()}`);
  return {
    learners: response.data.data.map(l => ({ ...l, groups: l.groups ?? [] })),
    pagination: response.data.meta,
  };
};

export const useLearners = (
  params: LearnersQueryParams = {}
): UseQueryResult<{ learners: Learner[]; pagination: Pagination }, Error> => {
  return useQuery({
    queryKey: ['learners', params],
    queryFn: () => fetchLearners(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  });
};

/**
 * Fetch all learners by paginating through all pages (max 100 per request).
 */
export const fetchAllLearners = async (): Promise<Learner[]> => {
  const PER_PAGE = 100;
  const first = await api.get<LearnersListResponse>(`/learners?page=1&per_page=${PER_PAGE}`);
  const learners = first.data.data;
  const pagination = first.data.meta;
  if (pagination.pages <= 1) return learners;

  const remaining = await Promise.all(
    Array.from({ length: pagination.pages - 1 }, (_, i) =>
      api.get<LearnersListResponse>(`/learners?page=${i + 2}&per_page=${PER_PAGE}`)
        .then(r => r.data.data)
    )
  );
  return learners.concat(...remaining);
};

export const useAllLearners = (): UseQueryResult<Learner[], Error> => {
  return useQuery({
    queryKey: ['learners', 'all'],
    queryFn: fetchAllLearners,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch single learner by ID
 * GET /api/v1/learners/:id
 */
export const fetchLearner = async (learnerId: string): Promise<Learner> => {
  const response = await api.get<ApiResponse<Learner>>(`/learners/${learnerId}`);
  return response.data.data!;
};

export const useLearner = (
  learnerId: string | undefined
): UseQueryResult<Learner, Error> => {
  return useQuery({
    queryKey: ['learners', learnerId],
    queryFn: () => fetchLearner(learnerId!),
    enabled: !!learnerId,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Fetch learner analytics
 * GET /api/v1/learners/:id/analytics
 */
export const fetchLearnerAnalytics = async (
  learnerId: string
): Promise<{ completion_stats: Record<string, unknown>; scores: Record<string, unknown>; time_spent: Record<string, unknown> }> => {
  const response = await api.get<ApiResponse<{
    completion_stats: Record<string, unknown>;
    scores: Record<string, unknown>;
    time_spent: Record<string, unknown>;
  }>>(`/learners/${learnerId}/analytics`);
  return response.data.data!;
};

/**
 * Nudge one learner about one unfinished course.
 *
 * Lands in their notifications AND as a direct message from the trainer who
 * sent it, so it can be answered (Aleix, 2026-09-21).
 */
export const remindLearnerAboutCourse = async ({
  learnerId,
  assignmentId,
  message,
}: {
  learnerId: string;
  assignmentId: string;
  message?: string;
}): Promise<{ chat_room_id: string | null; sent_at: string; message: string }> => {
  const response = await api.post<ApiResponse<{
    chat_room_id: string | null;
    sent_at: string;
    message: string;
  }>>(`/learners/${learnerId}/courses/${assignmentId}/remind`, { message });
  return response.data.data!;
};

export const useRemindLearnerAboutCourse = (learnerId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, message }: { assignmentId: string; message?: string }) =>
      remindLearnerAboutCourse({ learnerId: learnerId!, assignmentId, message }),
    onSuccess: () => {
      // The roster carries `last_reminded_at`, which has just changed.
      queryClient.invalidateQueries({ queryKey: ['learners', learnerId, 'analytics'] });
    },
  });
};

export const useLearnerAnalytics = (
  learnerId: string | undefined
) => {
  return useQuery({
    queryKey: ['learners', learnerId, 'analytics'],
    queryFn: () => fetchLearnerAnalytics(learnerId!),
    enabled: !!learnerId,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Create a new learner
 * POST /api/v1/learners
 */
export const createLearner = async (data: LearnerCreateData): Promise<Learner> => {
  const response = await api.post<ApiResponse<Learner>>('/learners', data);
  return response.data.data!;
};

export const useCreateLearner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLearner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learners'] });
    },
  });
};

/**
 * Bulk import learners
 * POST /api/v1/learners/bulk
 */
export const bulkImportLearners = async (
  data: BulkImportLearnerData
): Promise<BulkImportResponse['data']> => {
  const response = await api.post<BulkImportResponse>('/learners/bulk', data);
  return response.data.data;
};

export const useBulkImportLearners = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkImportLearners,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learners'] });
    },
  });
};

/**
 * Update an existing learner
 * PUT /api/v1/learners/:id
 */
export const updateLearner = async (
  learnerId: string,
  data: LearnerUpdateData
): Promise<Learner> => {
  const response = await api.put<ApiResponse<Learner>>(`/learners/${learnerId}`, data);
  return response.data.data!;
};

export const useUpdateLearner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ learnerId, data }: { learnerId: string; data: LearnerUpdateData }) =>
      updateLearner(learnerId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['learners', variables.learnerId] });
      queryClient.invalidateQueries({ queryKey: ['learners'] });
    },
  });
};

/**
 * Soft delete learner
 * DELETE /api/v1/learners/:id
 */
export const deleteLearner = async (learnerId: string): Promise<void> => {
  await api.delete(`/learners/${learnerId}`);
};

export const useDeleteLearner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteLearner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learners'] });
    },
  });
};

/**
 * Perform action on learner
 * POST /api/v1/learners/:id/actions
 */
export const performLearnerAction = async (
  learnerId: string,
  data: LearnerActionData
): Promise<SuccessResponse> => {
  const response = await api.post<{ data: SuccessResponse }>(`/learners/${learnerId}/actions`, data);
  return response.data.data;
};

export const useLearnerAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ learnerId, data }: { learnerId: string; data: LearnerActionData }) =>
      performLearnerAction(learnerId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['learners', variables.learnerId] });
      queryClient.invalidateQueries({ queryKey: ['learners'] });
    },
  });
};

// ─── Groups ─────────────────────────────────────────────────────────────

/**
 * Fetch paginated list of groups
 * GET /api/v1/groups
 */
export const fetchGroups = async (
  params: GroupsQueryParams = {}
): Promise<{ groups: LearnerGroup[]; pagination: Pagination }> => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);
  if (params.sort_order) queryParams.append('sort_order', params.sort_order);

  const response = await api.get<GroupsListResponse>(`/groups?${queryParams.toString()}`);
  return { groups: response.data.data, pagination: response.data.meta };
};

export const useGroups = (
  params: GroupsQueryParams = {}
): UseQueryResult<{ groups: LearnerGroup[]; pagination: Pagination }, Error> => {
  return useQuery({
    queryKey: ['groups', params],
    queryFn: () => fetchGroups(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch all groups by paginating through all pages (max 100 per request).
 */
export const fetchAllGroups = async (): Promise<LearnerGroup[]> => {
  const PER_PAGE = 100;
  const first = await api.get<GroupsListResponse>(`/groups?page=1&per_page=${PER_PAGE}`);
  const groups = first.data.data;
  const pagination = first.data.meta;
  if (pagination.pages <= 1) return groups;

  const remaining = await Promise.all(
    Array.from({ length: pagination.pages - 1 }, (_, i) =>
      api.get<GroupsListResponse>(`/groups?page=${i + 2}&per_page=${PER_PAGE}`)
        .then(r => r.data.data)
    )
  );
  return groups.concat(...remaining);
};

export const useAllGroups = (): UseQueryResult<LearnerGroup[], Error> => {
  return useQuery({
    queryKey: ['groups', 'all'],
    queryFn: fetchAllGroups,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch single group by ID with members
 * GET /api/v1/groups/:id
 */
export const fetchGroup = async (groupId: string): Promise<GroupDetail> => {
  const response = await api.get<GroupDetailResponse>(`/groups/${groupId}`);
  return response.data.data;
};

export const useGroup = (
  groupId: string | undefined
): UseQueryResult<GroupDetail, Error> => {
  return useQuery({
    queryKey: ['groups', groupId],
    queryFn: () => fetchGroup(groupId!),
    enabled: groupId !== undefined,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Create a new group
 * POST /api/v1/groups
 */
export const createGroup = async (data: GroupCreateData): Promise<LearnerGroup> => {
  const response = await api.post<ApiResponse<LearnerGroup>>('/groups', data);
  return response.data.data!;
};

export const useCreateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
};

/**
 * Update an existing group
 * PUT /api/v1/groups/:id
 */
export const updateGroup = async (
  groupId: string,
  data: GroupUpdateData
): Promise<LearnerGroup> => {
  const response = await api.put<ApiResponse<LearnerGroup>>(`/groups/${groupId}`, data);
  return response.data.data!;
};

export const useUpdateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: GroupUpdateData }) =>
      updateGroup(groupId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['groups', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
};

/**
 * Soft delete group and memberships
 * DELETE /api/v1/groups/:id
 */
export const deleteGroup = async (groupId: string): Promise<void> => {
  await api.delete(`/groups/${groupId}`);
};

export const useDeleteGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
};

/**
 * Add member to group
 * POST /api/v1/groups/:id/members
 */
export const addGroupMember = async (
  groupId: string,
  data: AddGroupMemberData
): Promise<SuccessResponse> => {
  const response = await api.post<{ data: SuccessResponse }>(`/groups/${groupId}/members`, data);
  return response.data.data;
};

export const useAddGroupMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: AddGroupMemberData }) =>
      addGroupMember(groupId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['groups', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['learners'] });
    },
  });
};

/**
 * Remove member from group
 * DELETE /api/v1/groups/:id/members/:userId
 */
export const removeGroupMember = async (groupId: string, userId: string): Promise<void> => {
  await api.delete(`/groups/${groupId}/members/${userId}`);
};

export const useRemoveGroupMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      removeGroupMember(groupId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['groups', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['learners'] });
    },
  });
};

/**
 * Get group analytics
 * GET /api/v1/groups/:id/analytics
 */
export const fetchGroupAnalytics = async (
  groupId: string
): Promise<{ member_count: number; completion_stats: Record<string, unknown>; average_scores: Record<string, unknown> }> => {
  const response = await api.get<ApiResponse<{
    member_count: number;
    completion_stats: Record<string, unknown>;
    average_scores: Record<string, unknown>;
  }>>(`/groups/${groupId}/analytics`);
  return response.data.data!;
};

export const useGroupAnalytics = (groupId: string | undefined) => {
  return useQuery({
    queryKey: ['groups', groupId, 'analytics'],
    queryFn: () => fetchGroupAnalytics(groupId!),
    enabled: groupId !== undefined,
    staleTime: 2 * 60 * 1000,
  });
};

// ─── Learner Roles & Locations (lookup tables) ─────────────────────────

/**
 * GET /api/v1/settings/learners/roles
 * Tenant-scoped learner role catalog (Manager, Specialist, etc.).
 */
export const fetchLearnerRoles = async (): Promise<LearnerRole[]> => {
  const response = await api.get<ApiResponse<LearnerRole[]>>(
    '/settings/learners/roles'
  );
  return response.data.data ?? [];
};

export const useLearnerRoles = (): UseQueryResult<LearnerRole[], Error> =>
  useQuery({
    // Same family as useLearnerAttributes('roles') in services/settings.ts, so
    // a label created in Organization invalidates this list too — they used
    // to be separate caches, and a new label took 5 minutes to appear in the
    // invitation pickers (Aleix, 2026-09-08).
    queryKey: ['settings', 'learners', 'roles', 'all'],
    queryFn: fetchLearnerRoles,
    staleTime: 5 * 60 * 1000,
  });

/**
 * GET /api/v1/settings/learners/locations
 * Tenant-scoped learner location catalog (HQ, Remote, etc.).
 */
export const fetchLearnerLocations = async (): Promise<LearnerLocation[]> => {
  const response = await api.get<ApiResponse<LearnerLocation[]>>(
    '/settings/learners/locations'
  );
  return response.data.data ?? [];
};

export const useLearnerLocations = (): UseQueryResult<LearnerLocation[], Error> =>
  useQuery({
    queryKey: ['settings', 'learners', 'locations', 'all'],
    queryFn: fetchLearnerLocations,
    staleTime: 5 * 60 * 1000,
  });

/**
 * GET /api/v1/settings/learners/departments
 * Tenant-scoped learner department catalog (Sales, Engineering, etc.).
 */
export const fetchLearnerDepartments = async (): Promise<LearnerDepartment[]> => {
  const response = await api.get<ApiResponse<LearnerDepartment[]>>(
    '/settings/learners/departments'
  );
  return response.data.data ?? [];
};

export const useLearnerDepartments = (): UseQueryResult<LearnerDepartment[], Error> =>
  useQuery({
    queryKey: ['settings', 'learners', 'departments', 'all'],
    queryFn: fetchLearnerDepartments,
    staleTime: 5 * 60 * 1000,
  });
